import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken, getUserProfile } from '../../utils/tokenManager';
import { useProfile } from '../../hooks/useProfile';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * ProtectedRoute component
 * Redirects to sign-in page if user is not authenticated
 * Automatically fetches user profile data on mount
 * Shows loading state until profile is loaded
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const [showLoading, setShowLoading] = useState(true);
  
  // Fetch user profile data (cached if already fetched)
  const { isLoading } = useProfile();
  
  useEffect(() => {
    const token = getAuthToken();
    
    if (!token) {
      console.log('❌ No authentication token found. Redirecting to sign-in...');
      navigate('/signin', { replace: true });
    }
  }, [navigate]);

  // Loading state is managed by authentication check

  // Check if user is authenticated
  const token = getAuthToken();
  
  if (!token) {
    return null; // Don't render anything while redirecting
  }

  // Check if profile is already cached (skip loading for subsequent visits)
  const cachedProfile = getUserProfile();
  
  // Show loading only on first load (when profile is being fetched and not cached)
  // Also respect the timeout
  if (isLoading && !cachedProfile && showLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#F8F9FA',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;

